import { Alert, Flex, message } from "antd";
import { useMemo, useState } from "react";
import ProceduralCoding from "../../components/ProceduralCoding/ProceduralCoding.component";
import { TransferProvider } from "../../components/TransferButton/TransferContext";
import TransferStatusBanner from "../../components/TransferButton/TransferStatusBanner";
import { DAP_FIELDS, NoteType, SOAP_FIELDS, getNotesType, shouldShowTreatmentPlan } from "../../domain/notes.domain";
import useCheckDemoPath from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useArtifactFocus } from "../../hooks/useArtifactFocus";
import { updateNote } from "../../service/notes.service";
import { setNotesDetails } from "../../slices/notesSlice";
import { EventType, NoteSection, trackEvent } from "../../utils/analytics";
import NoteChanges from "./NoteChanges.component";
import NoteSectionEdit from "./NoteSectionEdit";
import NoteTitleHeader from "./NoteTitleHeader.component";
import NotesSessionInsight from "./NotesSessionInsight.component";
import NudgeTreatmentPlan from "./NudgeTreatmentPlan.component";
import SessionDetails from "./SessionDetails.component";
import styles from "./notes.module.scss";

const BANNER_DISMISSED_KEY = "custom_template_banner_dismissed_at";
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const NotesView = () => {
  const componentName = "notes";

  const dispatch = useAppDispatch();
  const checkDemoPath = useCheckDemoPath();

  const { notesDetails } = useAppSelector((state) => state.notes);

  // "Slide open to the artifact" anchors — scrolled/highlighted when the
  // chat or post-session flow focuses a freshly generated artifact.
  // (Progress summary now lives pinned in the Superdoc chat, not here.)
  const noteFocusRef = useArtifactFocus("note");
  const insightFocusRef = useArtifactFocus("session_insights");
  const treatmentPlanFocusRef = useArtifactFocus("treatment_plan");
  const codesFocusRef = useArtifactFocus(["procedural_codes", "diagnostic_codes"]);

  const isBannerDismissed = useMemo(() => {
    const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissedAt) return false;
    return Date.now() - Number(dismissedAt) < TWENTY_FOUR_HOURS_MS;
  }, []);

  const [bannerVisible, setBannerVisible] = useState(!isBannerDismissed);

  const handleBannerClose = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, String(Date.now()));
    setBannerVisible(false);
  };

  const showCustomTemplateBanner = bannerVisible && notesDetails && notesDetails.note_type <= 13;

  const handleSaveClick = async () => {
    if (checkDemoPath()) {
      return;
    }
    if (notesDetails) {
      trackEvent(EventType.SAVE_NOTE, {
        recordingId: notesDetails?.id,
        noteType: getNotesType(notesDetails.note_type),
      });

      const updateNoteData = {
        audio_id: notesDetails?.id,
        data: notesDetails.data,
        subject: notesDetails.subject,
        objective: notesDetails.objective,
        assessment: notesDetails.assessment,
        plan: notesDetails.plan,
        intake_note: notesDetails.intake_note,
        eap_intake_note: notesDetails.eap_intake_note,
        psych_intake_note: notesDetails.psych_intake_note,
        psych_followup_note: notesDetails.psych_followup_note,
        emdr_note: notesDetails.emdr_note,
        sol_psych_intake_note: notesDetails.sol_psych_intake_note,
        sol_psych_followup_note: notesDetails.sol_psych_followup_note,
        sol_therapy_intake_note: notesDetails.sol_therapy_intake_note,
        sol_therapy_followup_note: notesDetails.sol_therapy_followup_note,
        document: notesDetails.document,
      };

      await updateNote(updateNoteData)
        .then((response) => {
          const { data } = response;
          dispatch(setNotesDetails(data));
          message.success("Note saved successfully");
        })
        .catch(() => {
          message.error("Error saving note");
        });
    }
  };

  if (!notesDetails) {
    return null;
  }

  return (
    <TransferProvider audioId={notesDetails.id}>
      <TransferStatusBanner />
      <NoteChanges />
      {notesDetails.note_type !== NoteType.CONSULT_NOTE && notesDetails.has_transcript && (
        <div ref={insightFocusRef}>
          <Flex gap={10} vertical>
            <NotesSessionInsight />
          </Flex>
        </div>
      )}

      {shouldShowTreatmentPlan(notesDetails) ? (
        <div ref={treatmentPlanFocusRef}>
          <NudgeTreatmentPlan />
        </div>
      ) : null}

      {(notesDetails?.location_type != null || notesDetails?.duration_type != null) && <SessionDetails />}

      {showCustomTemplateBanner && (
        <Alert
          showIcon={false}
          type="info"
          closable={{ closeIcon: true }}
          afterClose={handleBannerClose}
          style={{
            marginBottom: 20,
            alignItems: "baseline",
          }}
          message={
            <>
              We strongly recommend transitioning to our custom templates feature that has the power of ChatGPT-level
              customizations. It takes a few clicks: Click on the <strong>"Improve my note quality"</strong> button and
              then click <strong>"Create a custom..."</strong>. Follow the instructions and then click{" "}
              <strong>"Create Template"</strong>. You can now use this custom template.
            </>
          }
        />
      )}

      <div ref={noteFocusRef}>
        {notesDetails.note_type === NoteType.SOAP && (
          <NoteTitleHeader
            title="SOAP"
            hasCustomize={false}
            // @ts-ignore
          >
            <Flex vertical gap={20} className={`${styles[`${componentName}__notes_section_container`]}`}>
              {SOAP_FIELDS.map((field) => (
                <NoteSectionEdit
                  key={field.title}
                  title={field.title}
                  field={field.object}
                  analyticsField={field.analyticsField}
                  handleSaveClick={handleSaveClick}
                  hideHeader={false}
                  // @ts-ignore
                  value={notesDetails[field.object]}
                />
              ))}
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.Intake && (
          <NoteTitleHeader title="Intake" value={notesDetails.intake_note}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              {notesDetails && notesDetails.note_type === NoteType.Intake && (
                <NoteSectionEdit
                  title="Intake"
                  field="intake_note"
                  analyticsField={NoteSection.INTAKE_NOTES}
                  handleSaveClick={handleSaveClick}
                  value={notesDetails.intake_note}
                  bigger={true}
                />
              )}
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.EAP_INTAKE && (
          <NoteTitleHeader title="EAP Intake" value={notesDetails.eap_intake_note}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="EAP Intake"
                field="eap_intake_note"
                analyticsField={NoteSection.EAP_INTAKE_NOTES}
                handleSaveClick={handleSaveClick}
                value={notesDetails.eap_intake_note}
                bigger={true}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.PSYCHIATRIC_INTAKE && (
          <NoteTitleHeader title="Psychatric Intake" value={notesDetails.psych_intake_note}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Psychatric Intake"
                field="psych_intake_note"
                analyticsField={NoteSection.PSYCH_INTAKE_NOTES}
                handleSaveClick={handleSaveClick}
                value={notesDetails.psych_intake_note}
                bigger={true}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.DAP && (
          <NoteTitleHeader title="DAP" hasCustomize={false}>
            <Flex vertical gap={20} className={`${styles[`${componentName}__notes_section_container`]}`}>
              {DAP_FIELDS.map((field) => (
                <NoteSectionEdit
                  title={field.title}
                  field={field.object}
                  analyticsField={field.analyticsField}
                  handleSaveClick={handleSaveClick}
                  hideHeader={false}
                  // @ts-ignore
                  value={notesDetails[field.object]}
                />
              ))}
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails && notesDetails.note_type === NoteType.EMDR && (
          <NoteTitleHeader title="EMDR" value={notesDetails.emdr_note}>
            <Flex vertical className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="EMDR"
                field="emdr_note"
                analyticsField={NoteSection.EMDR_NOTES}
                handleSaveClick={handleSaveClick}
                value={notesDetails.emdr_note}
                bigger={true}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.SOL_PSYCH_INTAKE && (
          <NoteTitleHeader title="Psychiatric Intake" value={notesDetails.sol_psych_intake_note || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Psychiatric Intake"
                field="sol_psych_intake_note"
                analyticsField={NoteSection.SOL_PSYCH_INTAKE_NOTE}
                handleSaveClick={handleSaveClick}
                value={notesDetails.sol_psych_intake_note || ""}
                bigger={true}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.PSYCHIATRIC_FOLLOW_UP && (
          <NoteTitleHeader title="Psychiatric Followup" value={notesDetails.sol_psych_followup_note || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Psychiatric Followup"
                field="sol_psych_followup_note"
                analyticsField={NoteSection.SOL_THERAPY_FOLLOW_UP}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.sol_psych_followup_note || ""}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.THERAPY_INTAKE && (
          <NoteTitleHeader title="Therapy Intake" value={notesDetails.sol_therapy_intake_note || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Therapy Intake"
                field="sol_therapy_intake_note"
                analyticsField={NoteSection.SOL_THERAPY_INTAKE_NOTE}
                handleSaveClick={handleSaveClick}
                value={notesDetails.sol_therapy_intake_note || ""}
                bigger={true}
              />
            </Flex>
          </NoteTitleHeader>
        )}
        {notesDetails.note_type === NoteType.THERAPY_FOLLOW_UP_DAP ||
        notesDetails.note_type === NoteType.THERAPY_FOLLOW_UP_SOAP ? (
          <NoteTitleHeader title="Therapy Followup" value={notesDetails.sol_therapy_followup_note || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Therapy Followup"
                field="sol_therapy_followup_note"
                analyticsField={NoteSection.SOL_THERAPY_FOLLOW_UP}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.sol_therapy_followup_note || ""}
              />
            </Flex>
          </NoteTitleHeader>
        ) : null}
        {notesDetails.note_type === NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP ? (
          <NoteTitleHeader title="Psychiatric Followup (SOAP)" value={notesDetails.psych_followup_note || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Psychiatric Followup (SOAP)"
                field="psych_followup_note"
                analyticsField={NoteSection.PSYCH_FOLLOW_UP}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.psych_followup_note || ""}
              />
            </Flex>
          </NoteTitleHeader>
        ) : null}
        {notesDetails.note_type === NoteType.SOAP_SPEECH_THERAPY ? (
          <NoteTitleHeader title="SOAP (Speech Therapy)" value={notesDetails.document || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="SOAP (Speech Therapy)"
                field="document"
                analyticsField={NoteSection.SOAP_SPEECH_THERAPY}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.document || ""}
              />
            </Flex>
          </NoteTitleHeader>
        ) : null}
        {notesDetails.note_type === NoteType.CONSULT_NOTE ? (
          <NoteTitleHeader title="Consult Note" value={notesDetails.document || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title="Consult Note"
                field="document"
                analyticsField={NoteSection.CONSULT_NOTE}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.document || ""}
              />
            </Flex>
          </NoteTitleHeader>
        ) : null}
        {notesDetails.note_type > 13 ? (
          <NoteTitleHeader title={notesDetails?.note_type_info?.name || ""} value={notesDetails.document || ""}>
            <Flex className={`${styles[`${componentName}__notes_section_container`]}`}>
              <NoteSectionEdit
                title={notesDetails?.note_type_info?.name || ""}
                field="document"
                analyticsField={NoteSection.CUSTOM_NOTE}
                bigger={true}
                handleSaveClick={handleSaveClick}
                value={notesDetails.document || ""}
              />
            </Flex>
          </NoteTitleHeader>
        ) : null}
      </div>

      {notesDetails?.has_transcript && (
        <div ref={codesFocusRef}>
          <ProceduralCoding notesDetails={notesDetails} />
        </div>
      )}
    </TransferProvider>
  );
};

export default NotesView;
